import React from "react";

export default function ShippingPolicy() {
  return (
    <div className="container py-5">

      <h1 className="fw-bold mb-4">Shipping Policy</h1>

      <p className="text-muted" style={{ fontSize: "16px" }}>
        This Shipping Policy describes how <strong>SWIFTSHIP</strong> manages domestic and international
        shipments, including delivery timelines, tracking procedures, handling delays, customs clearance,
        and service limitations. By using our services, you acknowledge and agree to the terms outlined
        below.
      </p>

      <hr className="my-4" />

      {/* SECTION 1 */}
      <h3 className="fw-bold">1. Shipment Processing Time</h3>

      <p className="text-muted">
        All shipment requests are reviewed and processed during normal business hours. Processing time may
        vary depending on:
      </p>

      <ul className="text-muted">
        <li>✔ Shipment type (parcel, freight, consolidation, bulk cargo)</li>
        <li>✔ Pickup location and carrier availability</li>
        <li>✔ Documentation requirements for international shipments</li>
      </ul>

      <p className="text-muted">
        Orders submitted after business hours or on weekends may begin processing the next business day.
      </p>

      <hr className="my-4" />

      {/* SECTION 2 */}
      <h3 className="fw-bold">2. Delivery Timeframes (30–45 Days or Longer)</h3>

      <p className="text-muted">
        Due to the nature of domestic and international logistics, customers acknowledge that delivery
        timelines may require:
      </p>

      <ul className="text-muted">
        <li><strong>✔ 30–45 days or longer</strong> for international freight or consolidated shipments</li>
        <li>✔ 7–14 days for standard domestic freight (varies by region)</li>
        <li>✔ Longer delays during peak seasons, weather disruptions, customs inspections, or carrier routing issues</li>
      </ul>

      <p className="text-muted">
        These extended timeframes are standard for logistics operations and <strong>do not qualify as grounds
        for cancellation, refund, or chargeback</strong>.
      </p>

      <hr className="my-4" />

      {/* SECTION 3 */}
      <h3 className="fw-bold">3. Shipment Tracking & Updates</h3>

      <p className="text-muted">
        Customers will receive tracking information once the shipment is registered with a carrier. 
        Tracking visibility may vary by:
      </p>

      <ul className="text-muted">
        <li>✔ Carrier type (air, sea, road freight)</li>
        <li>✔ International customs handovers</li>
        <li>✔ Regional scanning facility availability</li>
      </ul>

      <p className="text-muted">
        Tracking may temporarily pause during international transitions or customs clearance.
      </p>

      <hr className="my-4" />

      {/* SECTION 4 */}
      <h3 className="fw-bold">4. Customs Clearance & Regulatory Delays</h3>

      <p className="text-muted">
        For international shipments, customs clearance procedures are mandatory and may cause delays. 
        SWIFTSHIP is not responsible for:
      </p>

      <ul className="text-muted">
        <li>✔ Import/export restrictions or inspections</li>
        <li>✔ Government documentation requirements</li>
        <li>✔ Duties, taxes, customs fees imposed by local authorities</li>
        <li>✔ Delays caused by incomplete or inaccurate customer documentation</li>
      </ul>

      <p className="text-muted">
        Customers are responsible for ensuring accurate information and complying with customs regulations.
      </p>

      <hr className="my-4" />

      {/* SECTION 5 */}
      <h3 className="fw-bold">5. Incorrect or Incomplete Address</h3>

      <p className="text-muted">
        If a shipment is delayed or returned due to an incorrect or incomplete address provided by the
        customer, additional charges may apply for:
      </p>

      <ul className="text-muted">
        <li>✔ Re-routing</li>
        <li>✔ Re-delivery</li>
        <li>✔ Storage</li>
        <li>✔ Carrier return fees</li>
      </ul>

      <p className="text-muted">
        SWIFTSHIP is not responsible for delays caused by incorrect customer information.
      </p>

      <hr className="my-4" />

      {/* SECTION 6 */}
      <h3 className="fw-bold">6. Lost, Damaged, or Delayed Shipments</h3>

      <p className="text-muted">
        Shipment handling is carried out by licensed third-party carriers. Claims must be filed according
        to carrier policy. Liability depends on:
      </p>

      <ul className="text-muted">
        <li>✔ Carrier investigation results</li>
        <li>✔ Insurance purchased by the customer</li>
        <li>✔ Proof of value and documented evidence</li>
      </ul>

      <p className="text-muted">
        SWIFTSHIP is not responsible for carrier delays, customs holds, weather conditions, or operational
        disruptions outside our control.
      </p>

      <hr className="my-4" />

      {/* SECTION 7 */}
      <h3 className="fw-bold">7. Non-Delivery Situations</h3>

      <p className="text-muted">
        Shipments may be marked as undeliverable due to:
      </p>

      <ul className="text-muted">
        <li>✔ Incorrect address</li>
        <li>✔ Recipient not available</li>
        <li>✔ Refused delivery</li>
        <li>✔ Customs rejection</li>
        <li>✔ Force majeure events</li>
      </ul>

      <p className="text-muted">
        Additional charges may apply for re-delivery or return logistics.
      </p>

      <hr className="my-4" />

      {/* SECTION 8 */}
      <h3 className="fw-bold">8. Chargebacks & Disputes</h3>

      <p className="text-muted">
        To prevent misuse of the dispute process, customers agree to:
      </p>

      <ul className="text-muted">
        <li>✔ Contact SWIFTSHIP to resolve concerns before initiating a chargeback</li>
        <li>✔ Understand that delays within disclosed timelines (30–45+ days) are not valid dispute grounds</li>
        <li>✔ Acknowledge that false chargebacks may result in account suspension or legal recovery</li>
        <li>✔ Accept that once a shipment is processed or picked up, service fees are non-refundable</li>
      </ul>

      <p className="text-muted">
        SWIFTSHIP maintains tracking logs, carrier scans, communication records, and verification data 
        as evidence in dispute handling.
      </p>

      <hr className="my-4" />

      {/* SECTION 9 */}
      <h3 className="fw-bold">9. Contact Information</h3>

      <p className="text-muted">
        For shipping questions, support, or documentation requests, contact:
      </p>

      <p className="mt-2">
        <strong>Email:</strong> info@swiftship.us <br />
        <strong>Address:</strong> 3800 Cabell Dr, Irving, TX 75063, United States <br />
        <strong>Phone:</strong> (480) 520-0274
      </p>

    </div>
  );
}
